/**
 * Iter 60 — 3-pose Face Enrolment Wizard.
 *
 * Steps:
 *   0. Pre-flight checklist (glasses off, cap off, white background, good light)
 *   1. Front-facing capture
 *   2. Left-profile capture (turn head slightly left)
 *   3. Right-profile capture (turn head slightly right)
 *   → POST all 3 descriptors in one shot to /api/mobile/attendance/enroll-face
 *
 * Matching uses the MIN euclidean distance across the 3 stored descriptors,
 * dramatically improving accuracy in real-world lighting / pose variations.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, ArrowRight, AlertTriangle, User as UserIcon } from 'lucide-react';
import FaceCaptureView from '../../components/FaceCaptureView';
import { useDeviceGuard, DesktopBlockScreen } from '../../components/DeviceGuard';
import { apiGet, apiPost } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { loadFaceModels } from '../../lib/faceRecognition';

const POSES = [
  { key: 'front', label: 'Front', hint: 'Look straight at the camera. Whole face in the ring.', tip: 'Hold your phone at eye level.' },
  { key: 'left',  label: 'Left',  hint: 'Turn your head slightly LEFT (about 30°).', tip: 'Keep your eyes on the camera if possible.' },
  { key: 'right', label: 'Right', hint: 'Turn your head slightly RIGHT (about 30°).', tip: 'Keep both eyes visible.' },
];

const CHECKLIST = [
  { id: 'glasses', text: 'Remove glasses, sunglasses or face shield' },
  { id: 'headwear', text: 'Remove cap, hat or scarf covering your forehead' },
  { id: 'background', text: 'Stand against a plain (white preferred) background' },
  { id: 'lighting', text: 'Face well-lit · no harsh shadow on one side' },
];

export default function FaceEnrollment() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const deviceAllowed = useDeviceGuard('enroll');
  const [employee, setEmployee] = useState(null);
  const [resolving, setResolving] = useState(true);
  const [step, setStep] = useState('checklist'); // 'checklist' | 'capture' | 'review' | 'submitting' | 'done'
  const [acknowledged, setAcknowledged] = useState({});
  const [poseIdx, setPoseIdx] = useState(0);
  const [captures, setCaptures] = useState([]); // [{pose, descriptor, photoDataUrl}]
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadFaceModels().catch(() => {});   // pre-warm models before first capture
    let cancelled = false;
    apiGet('/hr/me')
      .then((res) => { if (!cancelled) setEmployee(res?.linked && res?.employee ? res.employee : null); })
      .catch(() => { if (!cancelled) setEmployee(null); })
      .finally(() => { if (!cancelled) setResolving(false); });
    return () => { cancelled = true; };
  }, []);

  const allChecked = CHECKLIST.every((c) => acknowledged[c.id]);

  const onCapture = ({ descriptor, photoDataUrl }) => {
    const pose = POSES[poseIdx];
    setCaptures((prev) => {
      const next = [...prev.filter((c) => c.pose !== pose.key), { pose: pose.key, descriptor, photoDataUrl }];
      return next;
    });
    toast.success(`${pose.label} pose captured`);
    if (poseIdx < POSES.length - 1) {
      setPoseIdx(poseIdx + 1);
    } else {
      setStep('review');
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setStep('submitting');
    try {
      const front = captures.find((c) => c.pose === 'front') || captures[0];
      await apiPost('/mobile/attendance/enroll-face', {
        entity_type: 'employee',
        entity_id: employee.id,
        descriptor: front.descriptor,
        descriptors: captures.map((c) => c.descriptor),
        photo_url: front.photoDataUrl,
        photos: captures.map((c) => c.photoDataUrl),
      });
      setStep('done');
      toast.success('Face enrolled · 3 poses saved');
      setTimeout(() => navigate('/m/attend'), 1800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Enrolment failed');
      setStep('review');
    } finally { setSubmitting(false); }
  };

  // Field-only gate: face enrolment from a desktop defeats the control.
  if (deviceAllowed === null) return null;
  if (deviceAllowed === false) return <DesktopBlockScreen />;

  if (resolving) {
    return <CenterStage><Loader2 size={32} className="animate-spin" /></CenterStage>;
  }
  if (!employee) {
    return (
      <CenterStage>
        <div className="text-center px-6">
          <h2 className="font-display font-bold text-xl mb-2">Account not linked</h2>
          <p className="text-sm text-white/60 mb-4">
            Your login email <strong className="text-white/80">{user?.email}</strong> isn't yet linked to an employee record.
            Ask HR to add you to Employee Master with the same email, then refresh.
          </p>
          <button onClick={() => navigate('/app')} className="bg-white text-slate-900 px-5 py-2 rounded-full font-display font-bold text-sm" data-testid="face-not-linked-back">Back</button>
        </div>
      </CenterStage>
    );
  }

  if (step === 'done') {
    return (
      <CenterStage>
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500 text-white flex items-center justify-center mb-4 shadow-2xl">
            <Check size={40} />
          </div>
          <h2 className="font-display font-bold text-2xl">Face enrolled</h2>
          <p className="text-sm text-white/60 mt-1">3 poses saved · matching accuracy boosted. Redirecting…</p>
        </div>
      </CenterStage>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f1a2e] via-[#1e2a4d] to-[#0f1a2e] text-white">
      <header className="px-5 pt-7 pb-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-display font-bold">One-time setup</div>
        <div className="font-display font-bold text-2xl mt-0.5">Enrol your face</div>
        <p className="text-sm text-white/60 mt-1">
          We'll capture <strong className="text-white/90">3 poses</strong> (front, left, right) for higher accuracy.
          The whole process takes ~30 seconds.
        </p>
      </header>

      {step === 'checklist' && (
        <main className="px-5 pb-10 space-y-3">
          <div className="bg-amber-500/10 border border-amber-400/40 rounded-2xl p-4 flex gap-3" data-testid="face-checklist">
            <AlertTriangle size={20} className="text-amber-300 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-100">
              <div className="font-display font-bold mb-1">Pre-flight checklist</div>
              <div className="text-xs text-amber-100/80">Tick everything before continuing — these dramatically improve future matching accuracy.</div>
            </div>
          </div>
          {CHECKLIST.map((item) => (
            <label key={item.id} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl p-4 cursor-pointer hover:bg-white/10 transition-colors" data-testid={`face-check-${item.id}`}>
              <input
                type="checkbox"
                checked={!!acknowledged[item.id]}
                onChange={(e) => setAcknowledged((a) => ({ ...a, [item.id]: e.target.checked }))}
                className="w-5 h-5 accent-emerald-400"
              />
              <span className="text-sm">{item.text}</span>
            </label>
          ))}
          <div className="bg-white/5 border border-dashed border-white/15 rounded-2xl p-3 text-[11px] text-white/50 flex items-center gap-2">
            <UserIcon size={13} /> Enrolling for: <strong className="text-white/80">{employee.name}</strong>
          </div>
          <button
            onClick={() => setStep('capture')}
            disabled={!allChecked}
            data-testid="face-checklist-continue"
            className="w-full bg-white text-slate-900 font-display font-bold text-base py-4 rounded-full shadow-2xl active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            I'm ready · Start <ArrowRight size={16} />
          </button>
          <button onClick={() => navigate('/m/attend')} className="w-full text-center text-xs text-white/40 py-2">Skip for now</button>
        </main>
      )}

      {step === 'capture' && (
        <main className="px-5 pb-10">
          <PoseStepper current={poseIdx} captured={captures.map((c) => c.pose)} />
          <div className="bg-white/5 border border-white/10 rounded-2xl p-3 mb-3" data-testid="face-pose-hint">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-display font-bold">Pose {poseIdx + 1} of 3 — {POSES[poseIdx].label}</div>
            <div className="text-sm font-display font-bold mt-1">{POSES[poseIdx].hint}</div>
            <div className="text-[11px] text-white/50 mt-1">{POSES[poseIdx].tip}</div>
          </div>
          <FaceCaptureView
            key={POSES[poseIdx].key /* remount so auto-capture re-arms */}
            onCapture={onCapture}
            busy={submitting}
            captureLabel={`Capture ${POSES[poseIdx].label}`}
            helpText={`${POSES[poseIdx].label} pose — hold steady while we verify…`}
            requiredPose={POSES[poseIdx].key}
          />
          {poseIdx > 0 && (
            <button onClick={() => setPoseIdx(poseIdx - 1)} className="mt-3 w-full text-center text-xs text-white/50 py-2" data-testid="face-pose-back">
              Back to {POSES[poseIdx - 1].label} pose
            </button>
          )}
        </main>
      )}

      {step === 'review' && (
        <main className="px-5 pb-10 space-y-4">
          <div className="text-sm text-white/70 text-center">3 poses captured. Review and submit.</div>
          <div className="grid grid-cols-3 gap-2" data-testid="face-review-grid">
            {POSES.map((p) => {
              const cap = captures.find((c) => c.pose === p.key);
              return (
                <div key={p.key} className="bg-white/5 border border-white/10 rounded-xl p-2 text-center">
                  <div className="text-[9px] uppercase tracking-widest text-white/50 font-display font-bold mb-1">{p.label}</div>
                  {cap?.photoDataUrl ? (
                    <img src={cap.photoDataUrl} alt={p.label} className="w-full aspect-square object-cover rounded-md" />
                  ) : (
                    <div className="w-full aspect-square bg-white/5 rounded-md flex items-center justify-center text-white/30 text-xs">missing</div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="face-review-submit"
            className="w-full bg-emerald-500 text-white font-display font-bold text-base py-4 rounded-full shadow-2xl active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <><Check size={16} /> Save All 3 Poses</>}
          </button>
          <button onClick={() => { setCaptures([]); setPoseIdx(0); setStep('capture'); }} className="w-full text-center text-xs text-white/50 py-2" data-testid="face-review-retake">
            Re-capture all 3
          </button>
        </main>
      )}

      {step === 'submitting' && (
        <CenterStage><Loader2 size={32} className="animate-spin" /><div className="mt-3 text-sm text-white/70">Saving 3 descriptors…</div></CenterStage>
      )}
    </div>
  );
}

function CenterStage({ children }) {
  return <div className="min-h-screen bg-gradient-to-br from-[#0f1a2e] via-[#1e2a4d] to-[#0f1a2e] text-white flex flex-col items-center justify-center">{children}</div>;
}

function PoseStepper({ current, captured }) {
  return (
    <div className="flex items-center gap-2 mb-3" data-testid="face-pose-stepper">
      {POSES.map((p, i) => {
        const isDone = captured.includes(p.key);
        const isActive = i === current;
        return (
          <React.Fragment key={p.key}>
            <div className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded-full text-[10px] font-display font-bold uppercase tracking-wider ${
              isDone ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/30'
              : isActive ? 'bg-white/15 text-white border border-white/30'
              : 'bg-white/5 text-white/40 border border-white/10'
            }`}>
              <span>{isDone ? <Check size={10} /> : i + 1}</span> {p.label}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
